import React, { useMemo, useRef, useState, useEffect } from 'react';

import { Chip, MenuItem, Select, TableBody } from '@material-ui/core';
import SkeletonLoader from 'components/SkeletonLoader';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { FlexContainer, StyledBorderedPaper, StyledTooltip, VerticallyAlignedFlexContainer } from 'styles/common';

import { CloseIcon2, DownChevronSvgIcon } from 'constants/icons';
import theme from 'constants/theme';

import { getOrganizationFunctions } from 'services/admin';
import { getCompetencySummary } from 'services/competencies';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography12, Typography13, Typography14, Typography2 } from 'uiToolkit/Typography';
import { Paper } from 'uiToolkitV1/Paper';

import { findObject, getArrayOfIdsOfParentFunctions } from '../../FunctionAndGrade/Helpers';
import { StyledCompetencyHeaderCell } from '../common/CommonComponents';
import TreeViewComponent from '../common/TreeViewComponent';
import NoCompetencyComponent from './NoCompetencyComponent';
import CompetencyFunctionAndLevelRow from './common/CompetencyFunctionAndLevelRow';
import CompetencyHeader from './common/CompetencyHeader';
import CompetencyTableHeader from './common/CompetencyTableHeader';

const getSelectedFunctionTree = ({ functions = [], jobFunctionId }) => {
  const jobFunctionObject = findObject(functions ?? [], String(jobFunctionId));

  if (!jobFunctionObject?.parentId) {
    return jobFunctionObject?.name;
  }

  const arrayIds = getArrayOfIdsOfParentFunctions(functions, jobFunctionObject)?.reverse();
  const stringifiedFunctions =
    arrayIds?.reduce((prev, curr) => `${prev + curr?.name} > `, '') + jobFunctionObject?.name;
  return stringifiedFunctions;
};

export const StyledFunctionSelect = styled(Select)`
  ${(props) =>
    props.$isAccessControl &&
    css`
      margin-right: 1.5rem;
    `}
  ${(props) =>
    props.$isDropdownOnly &&
    !props.$isAccessControl &&
    css`
      margin-top: 9px;
    `}
  ${(props) =>
    !props.$isDropdownOnly &&
    css`
      width: 240px;
      font-size: 14px;
      .MuiOutlinedInput-notchedOutline {
        border: 1px solid ${(props) => props.theme.palette.primary.main} !important;
      }
    `}
  height: 40px;

  .MuiMenu-paper {
    max-height: 50vh;
    overflow-y: scroll;
  }
  .MuiSelect-outlined {
    padding: 0.5rem 1rem;

    background-color: white !important;

    &:focus {
      background-color: white !important;
    }
  }

  .MuiSelect-icon {
    color: ${(props) => props.theme.palette.primary.main};
  }
`;

const FunctionDropdown = ({
  setSelectedFunction,
  selectedFunction = null,
  functions = [],
  loadingFunctions,
  isOpen,
  setIsOpen,
  isDropdownOnly = false,
  isAccessControl = false,
  selectedFunctionsArray = [],
  selectedFunctions = {},
  onChipDelete = () => {},
  placeholder = 'Function',
  isRowClickable = false,
}) => (
  <StyledFunctionSelect
    fullWidth
    displayEmpty
    variant="outlined"
    id="job-function-select"
    placeholder={placeholder}
    onChange={(e) => setSelectedFunction(e.target.value)}
    value={isAccessControl ? selectedFunctionsArray : selectedFunction}
    $isDropdownOnly={isDropdownOnly}
    $isAccessControl={isAccessControl}
    renderValue={(value) => {
      if (isAccessControl) {
        if (!value?.length) {
          return <Typography2 customColor={theme.palette.text.label}>Select Function</Typography2>;
        }
        return (
          <>
            {value?.map(({ id, displayName }) => (
              <Chip
                style={{ marginRight: '0.35rem' }}
                key={id}
                label={displayName}
                clickable={false}
                deleteIcon={
                  <CloseIcon2
                    rightGutter={0.5}
                    leftGutter={0.5}
                    size="fixed12px"
                    color={theme.palette.icons.grey2}
                    clickable
                  />
                }
                onDelete={(e) => {
                  e.stopPropagation();
                  onChipDelete({ uuid: id, name: displayName });
                }}
              />
            ))}
          </>
        );
      }
      if (value === null) {
        return <Typography2 customColor={theme.palette.primary.main}>All functions</Typography2>;
      }

      if (isDropdownOnly) {
        const selectedFunctionString = getSelectedFunctionTree({
          functions,
          jobFunctionId: value,
        });
        const isPlaceholderVisible = !selectedFunctionString;
        return (
          <StyledTooltip title={isPlaceholderVisible ? '' : selectedFunctionString}>
            {isPlaceholderVisible ? (
              <Typography2 customColor="hsl(0,0%,50%)">{placeholder}</Typography2>
            ) : (
              <span> {selectedFunctionString}</span>
            )}
          </StyledTooltip>
        );
      }
      const selectedFunction = findObject(functions ?? [], value);
      return <Typography2 customColor={theme.palette.primary.main}>{selectedFunction?.name}</Typography2>;
    }}
    MenuProps={{
      disablePortal: true,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
      getContentAnchorEl: null,
    }}
    style={{ marginLeft: 'auto' }}
    disabled={loadingFunctions}
    open={isOpen}
    onClick={() => {
      setIsOpen((prev) => !prev);
    }}
    IconComponent={() => (
      <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <VerticalDivider />
        <div style={{ padding: '8px', display: 'flex' }}>
          <DownChevronSvgIcon color="#ccc" size="fixed20px" />
        </div>
      </div>
    )}
    multiple={isAccessControl}
  >
    <MenuItem value="" disabled style={{ display: 'none' }}>
      <Typography13 color="primary"> Select </Typography13>
    </MenuItem>
    {!isDropdownOnly && (
      <MenuItem value={null} disabled style={{ display: 'none' }}>
        <Typography13 color="primary"> All functions </Typography13>
      </MenuItem>
    )}
    <TreeViewComponent
      setSelectedFunction={setSelectedFunction}
      selectedFunction={selectedFunction}
      functions={functions}
      loadingFunctions={loadingFunctions}
      setIsOpen={setIsOpen}
      isDropdownOnly={isDropdownOnly}
      isAccessControl={isAccessControl}
      selectedFunctions={selectedFunctions}
      isRowClickable={isRowClickable}
    />
  </StyledFunctionSelect>
);

export const FunctionSelection = ({
  selectedFunction = null,
  setSelectedFunction,
  isDropdownOnly = false,
  isCohortCreation = false,
  selectedFunctions = {},
  setAddNew = () => {},
  isAccessControl = false,
  selectedFunctionsArray = [],
  onChipDelete = () => {},
  placeholder = 'Function',
  isRowClickable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // functions and levels
  const dispatch = useDispatch();
  const [functions, setFunctions] = useState(null);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const fetchData = () => {
    setLoadingFunctions(true);
    getOrganizationFunctions()
      .then((res) => {
        setFunctions(res);
      })
      .catch((err) => {
        console.error('Error in fetching functions : ', err);
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => {
        setLoadingFunctions(false);
      });
  };
  useEffect(() => {
    fetchData();
  }, []);
  // end of functions and levels

  if (isCohortCreation) {
    return (
      <TreeViewComponent
        setSelectedFunction={setSelectedFunction}
        selectedFunction={selectedFunction}
        functions={functions}
        loadingFunctions={loadingFunctions}
        isCohortCreation={isCohortCreation}
        selectedFunctions={selectedFunctions}
      />
    );
  }

  if (isDropdownOnly) {
    return (
      <FunctionDropdown
        setSelectedFunction={setSelectedFunction}
        selectedFunction={selectedFunction}
        functions={functions}
        loadingFunctions={loadingFunctions}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isDropdownOnly
        placeholder={placeholder}
        isRowClickable={isRowClickable}
      />
    );
  }

  if (isAccessControl) {
    return (
      <FunctionDropdown
        setSelectedFunction={setSelectedFunction}
        selectedFunction={selectedFunction}
        functions={functions}
        loadingFunctions={loadingFunctions}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isDropdownOnly
        isAccessControl
        selectedFunctions={selectedFunctions}
        selectedFunctionsArray={selectedFunctionsArray}
        onChipDelete={onChipDelete}
      />
    );
  }

  return (
    <BorderedPaper>
      <VerticallyAlignedFlexContainer>
        <FlexContainer style={{ flexDirection: 'column' }}>
          <Typography14>Select the function for which you want to add competencies</Typography14>
          <DescriptionContainer>
            <Typography12 customColor={theme.palette.text.label}>
              Please input all organization-wide and specialized competencies applicable to employees. You can add rows
              for additional competencies using the icon at the bottom of the screen for all the unique designations in
              your organization.
            </Typography12>
          </DescriptionContainer>
        </FlexContainer>
        <FunctionDropdown
          setSelectedFunction={(v) => {
            if (setAddNew) {
              setAddNew(false);
            }
            setSelectedFunction(v);
          }}
          selectedFunction={selectedFunction}
          functions={functions}
          loadingFunctions={loadingFunctions}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      </VerticallyAlignedFlexContainer>
    </BorderedPaper>
  );
};

const CompetencyByFunctionAndRole = ({
  tagType,
  tagTypeLabel,
  singularTagTypeLabel,
  tagLabel,
  isEnterpriseAccessOnly,
  allowDescriptionNewline = false,
  setShowSettingsScreen,
  originalTagType,
  openCompetencyUploadSidePanel,
}) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const tableHeaderRef = useRef();

  const scrollTableHeaderIntoView = () => {
    tableHeaderRef.current.scrollIntoView();
  };

  const [addNew, setAddNew] = useState(false);

  const addNewTag = () => {
    setAddNew(true);
    const updatedCompetencies = ['newComp', ...(competencySummary?.competencies ?? [])];
    setCompetencySummary((prev) => ({ ...prev, competencies: updatedCompetencies }));
  };

  const [selectedFunction, setSelectedFunction] = useState(null);

  const [competencySummary, setCompetencySummary] = useState(null);

  const [loadingCompetencySummary, setLoadingCompetencySummary] = useState(true);

  const fetchCompetencySummary = () => {
    setLoadingCompetencySummary(true);
    getCompetencySummary(selectedFunction)
      .then((res) => setCompetencySummary(res))
      .finally(() => {
        setLoadingCompetencySummary(false);
      });
  };

  useEffect(() => {
    fetchCompetencySummary();
  }, [selectedFunction]);

  const removeNewTag = () => {
    setAddNew(false);
    const updatedCompetencies = competencySummary.competencies.filter((id) => id !== 'newComp');
    setCompetencySummary((prev) => ({ ...prev, competencies: updatedCompetencies }));
  };

  const roles = useMemo(() => competencySummary?.roles ?? [], [competencySummary]);

  if (loadingCompetencySummary) {
    return (
      <Paper p={3} mt={1}>
        <CompetencyTableHeader
          addNew={addNew}
          addNewTag={addNewTag}
          selectedFunction={selectedFunction}
          setAddNew={setAddNew}
          setSelectedFunction={setSelectedFunction}
          tableHeaderRef={tableHeaderRef}
          openCompetencyUploadSidePanel={openCompetencyUploadSidePanel}
          showUploadButton
        />
        <BorderedPaper>
          <SkeletonLoader skeletons={['100%', '100%', '100%', '100%']} />
        </BorderedPaper>
      </Paper>
    );
  }

  return (
    <Paper p={3}>
      <CompetencyTableHeader
        addNew={addNew}
        addNewTag={addNewTag}
        selectedFunction={selectedFunction}
        setAddNew={setAddNew}
        setSelectedFunction={setSelectedFunction}
        tableHeaderRef={tableHeaderRef}
        openCompetencyUploadSidePanel={openCompetencyUploadSidePanel}
        showUploadButton
      />

      <OverflowContainer>
        <StyledRoleHeader>
          <StyledCompetencyHeaderCell fixed>
            <Typography2 color="textSecondary" weight="bold">
              Competencies for {competencySummary?.function?.name ?? 'All Functions'}
            </Typography2>
          </StyledCompetencyHeaderCell>

          {!competencySummary?.function && !competencySummary?.role && (
            <StyledCompetencyHeaderCell>
              <Typography2 color="textSecondary" weight="bold">
                Description
              </Typography2>
            </StyledCompetencyHeaderCell>
          )}

          <CompetencyHeader roles={roles} />
        </StyledRoleHeader>
        {/* <BorderedPaper style={{ padding: 0 }}> */}
        {!competencySummary?.competencies?.length && <NoCompetencyComponent />}
        <TableBody>
          {competencySummary?.competencies?.map((compId) => (
            <CompetencyFunctionAndLevelRow
              key={compId}
              id={compId}
              functionId={selectedFunction}
              roles={competencySummary?.roles}
              fetchCompetencySummary={fetchCompetencySummary}
              addNew={addNew}
              removeNewTag={removeNewTag}
              setCompetencySummary={setCompetencySummary}
              setAddNew={setAddNew}
              scrollTableHeaderIntoView={scrollTableHeaderIntoView}
              competencies={competencySummary?.competencies}
            />
          ))}
        </TableBody>
        {/* </BorderedPaper> */}
      </OverflowContainer>
    </Paper>
  );
};

const BorderedPaper = styled(StyledBorderedPaper)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const DescriptionContainer = styled.div`
  max-width: 40rem;
`;

const StyledRoleHeader = styled.div`
  display: flex;
  width: min-content;
  position: sticky;
  top: 0px;
  z-index: 2;

  > div:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  > div:last-child {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const OverflowContainer = styled.div`
  overflow-y: auto;
  overflow-x: auto;
  height: 100%;
  max-height: 90vh;
  position: relative;
`;

const VerticalDivider = styled.span`
  height: 1.3rem;
  width: 0px;
  border-right: 1px solid #ccc;
  margin-left: 0.5rem;
  margin-right: 0rem;
`;

export default CompetencyByFunctionAndRole;
